/**
 * CommandPalette — buscador global de encomiendas (Ctrl+K), teléfono primero.
 *
 * Decisiones de diseño:
 * - Objetivo de rendimiento del brief: teléfono → encomienda en menos de 2
 *   segundos. Por eso la búsqueda es "phone-first": si la consulta trae ≥3
 *   dígitos se compara contra los dígitos del teléfono del cliente; si no,
 *   se hace fuzzy match (insensible a mayúsculas y acentos) sobre el nombre.
 * - Con consulta vacía muestra las 8 encomiendas de mayor prioridad de triaje
 *   (STATUS_META.priority, luego actividad más reciente): el palette también
 *   sirve como "siguiente tarea" sin escribir nada.
 * - El palette carga su propia lista vía listDrafts() al abrirse: no depende
 *   del estado de ninguna pestaña, se puede invocar desde cualquier lugar.
 * - Teclado completo: flechas + Enter + Esc. roles listbox/option con
 *   aria-selected para lectores de pantalla.
 * - Máximo 8 resultados: el operador escanea, no pagina.
 */
import { useEffect, useRef, useState } from 'react';
import { Search, PackageSearch } from 'lucide-react';
import { listDrafts } from '../../../api/mock/recepcionDigital';
import StatusChip, { STATUS_META } from './StatusChip';
import EmptyState from './EmptyState';

const soloDigitos = (s) => (s || '').replace(/\D/g, '');

const normalizar = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Subsecuencia: "mro" encuentra "María Rodríguez". Substring también cuenta.
const fuzzyNombre = (query, nombre) => {
  const q = normalizar(query);
  const n = normalizar(nombre);
  if (!q) return true;
  if (n.includes(q)) return true;
  let i = 0;
  for (const ch of n) {
    if (ch === q[i]) i++;
    if (i === q.length) return true;
  }
  return false;
};

const ordenPrioridad = (a, b) => {
  const pa = STATUS_META[a.status]?.priority ?? 99;
  const pb = STATUS_META[b.status]?.priority ?? 99;
  if (pa !== pb) return pa - pb;
  return new Date(b.last_client_activity_at || 0) - new Date(a.last_client_activity_at || 0);
};

const CommandPalette = ({ open, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const cargar = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await listDrafts();
      setDrafts(data || []);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedIndex(0);
    cargar();
    // El input puede no existir hasta el próximo tick de render.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const digitos = soloDigitos(query);
  let resultados;
  if (digitos.length >= 3) {
    resultados = drafts.filter((d) => soloDigitos(d.client?.phone).includes(digitos));
  } else if (query.trim()) {
    resultados = drafts.filter((d) => fuzzyNombre(query, d.client?.name));
  } else {
    resultados = [...drafts].sort(ordenPrioridad);
  }
  resultados = resultados.slice(0, 8);

  const seleccionar = (draft) => {
    if (!draft) return;
    onSelect && onSelect(draft.id);
    onClose && onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, resultados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      seleccionar(resultados[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose && onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]"
      style={{ background: 'rgba(15, 18, 22, 0.45)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Buscador de encomiendas"
        className="w-full max-w-xl rounded-xl border shadow-xl"
        style={{
          background: 'var(--tile)',
          borderColor: 'var(--border)',
          boxShadow: 'var(--tile-shadow)',
        }}
      >
        <div
          className="flex items-center gap-2 border-b px-4"
          style={{ borderColor: 'var(--border)' }}
        >
          <Search size={15} style={{ color: 'var(--text-tertiary)' }} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Buscar por teléfono o nombre del cliente…"
            aria-label="Buscar encomienda por teléfono o nombre del cliente"
            className="min-h-[44px] w-full bg-transparent text-sm
                       focus-visible:outline-none"
            style={{ color: 'var(--text-main)' }}
          />
          <kbd
            className="rounded border px-1.5 py-0.5 text-[10px]"
            style={{ borderColor: 'var(--border)', color: 'var(--text-tertiary)' }}
          >
            Esc
          </kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto p-2">
          {loading && (
            <p className="px-3 py-6 text-center text-xs" style={{ color: 'var(--text-secondary)' }}>
              Cargando…
            </p>
          )}

          {!loading && error && (
            <div className="px-3 py-6 text-center">
              <p className="text-xs" style={{ color: 'var(--avianca-red)' }} role="alert">
                No se pudieron cargar las encomiendas.
              </p>
              <button
                type="button"
                onClick={cargar}
                aria-label="Reintentar la carga de encomiendas"
                className="mt-2 min-h-[44px] rounded-md border px-4 py-2 text-xs font-medium
                           hover:bg-[var(--nav-active-bg)]
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
              >
                Reintentar
              </button>
            </div>
          )}

          {!loading && !error && resultados.length === 0 && (
            <EmptyState
              icon={PackageSearch}
              title="Sin resultados para su búsqueda."
              subtitle="Pruebe con otro teléfono u otro nombre de cliente."
            />
          )}

          {!loading && !error && resultados.length > 0 && (
            <ul role="listbox" aria-label="Resultados de encomiendas">
              {resultados.map((d, i) => (
                <li key={d.id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === selectedIndex}
                    onClick={() => seleccionar(d)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    className="flex min-h-[44px] w-full items-center gap-3 rounded-md px-3 py-2 text-left
                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                    style={{
                      background: i === selectedIndex ? 'var(--nav-active-bg)' : 'transparent',
                    }}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium" style={{ color: 'var(--text-main)' }}>
                        {d.client?.name || 'Sin nombre'}
                      </p>
                      <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                        {d.client?.phone}
                      </p>
                    </div>
                    <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                      {(d.packages || []).length} paq.
                    </span>
                    <StatusChip status={d.status} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;

/*
Ejemplo de uso (en el shell del módulo, con Ctrl+K global):
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  <CommandPalette
    open={paletteOpen}
    onClose={() => setPaletteOpen(false)}
    onSelect={(draftId) => abrirEnProduccion(draftId)}
  />
*/
